import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonComponent } from '@shared/components/button/button.component';
import { FormErrorsModule } from '@shared/components/form-elements/form-errors/form-errors.module';
import { InputFieldComponent } from '@shared/components/form-elements/input-field/input-field.component';
import { LocalLoaderComponent } from '@shared/components/loader/local-loader/local-loader.component';
import { ToasterModule } from '@shared/components/toaster/toaster.module';
import { AuthService } from '@app/core/authentication/services/auth.service';
import { CX_COMPONENT } from '@app/core/models/atomics.model';
import { UserService } from '@app/core/user/user.service';
import { ForgotPasswordService } from '@b2b/features/forgot-password-dialog/core/forgot-password.service';
import { TermsModalService } from '@b2b/features/misc/terms-modal/terms-modal.service';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, take } from 'rxjs';
import { CmsComponentPipe } from '@app/b2b/core/cms/pipes/cms-component.pipe';
import { CxAbstractComponent } from '@b2b/components/cms/cx-abstract/cx-abstract-component/cx-abstract.component';
import { CX_ABSTRACT_SECTION_COMPONENTS } from '@b2b/components/cms/cx-abstract/cx-abstract.tokens';
import { B2BUnitService } from '@b2b/features/b2b-unit/core/b2b-unit.service';

@Component({
  selector: 'cx-login-section',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ToasterModule,
    FormErrorsModule,
    ButtonComponent,
    InputFieldComponent,
    LocalLoaderComponent,
    CxAbstractComponent,
    CmsComponentPipe,
  ],
  templateUrl: './cx-login-section.component.html',
  styleUrl: './cx-login-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CxLoginSectionComponent {
  protected components = inject<any[]>(CX_ABSTRACT_SECTION_COMPONENTS);

  CX_COMPONENT = CX_COMPONENT;

  form = new FormGroup({
    user: new FormControl(null, [Validators.required]),
    password: new FormControl(null, [Validators.required]),
  });

  pwIsVisible = false;
  pwInputType = 'password';
  invalidGrant$ = new BehaviorSubject<boolean>(false);
  isLoading$ = new BehaviorSubject<boolean>(false);
  errorMessage = '';

  constructor(
    private auth: AuthService,
    private forgotPasswordService: ForgotPasswordService,
    private userService: UserService,
    private b2bUnitService: B2BUnitService,
    private termsModalService: TermsModalService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  handlerVisibility() {
    this.pwIsVisible = !this.pwIsVisible;
    this.pwInputType = this.pwIsVisible ? 'text' : 'password';
  }

  openForgotPassword() {
    this.forgotPasswordService.openModal({
      user: this.form.get('user')?.value || undefined,
    });
  }

  submit() {
    this.invalidGrant$.next(false);

    if (!this.form.valid) {
      return;
    }

    this.isLoading$.next(true);
    this.cdr.markForCheck();
    const { user, password } = this.form.value;

    this.auth
      .login(user!, password!)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.termsModalService.load();
          this.userService.load();
          this.b2bUnitService.load();
          this.router.navigate(['/']);
        },
        error: (error: HttpErrorResponse) => {
          this.invalidGrant$.next(true);
          this.errorMessage = error?.error?.message || 'Falha no login';
          this.isLoading$.next(false);
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isLoading$.next(false);
          this.cdr.markForCheck();
        },
      });
  }
}
